import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaChevronLeft, FaShareAlt, FaTwitter, FaFacebook, FaLinkedin } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate scrolling to top when component mounts
    window.scrollTo(0, 0);
    
    // In a real app, this would fetch data from an API
    // For now, we'll use mock data
    const fetchPost = async () => {
      setLoading(true);
      
      // Mock blog post data
      const mockPost = {
        id: 1,
        slug: slug,
        title: "The Future of AI Development in 2023 and Beyond",
        excerpt: "Exploring emerging trends in artificial intelligence, from large language models to multimodal systems, and what they mean for developers and businesses.",
        coverImage: "/blog/ai-future.jpg",
        publishedAt: "June 15, 2023",
        readTime: "8 min read",
        author: {
          name: "Dhiraj T H",
          avatar: "/profile-image.png",
          bio: "AI Engineer & Innovator"
        },
        content: `
          <h2>Introduction</h2>
          <p>The field of artificial intelligence has seen unprecedented growth in the past few years. What was once the domain of specialized researchers is now accessible to developers across the globe. In this post, we'll explore the most significant trends shaping AI development in 2023 and look at what the future holds.</p>
          
          <h2>The Rise of Large Language Models</h2>
          <p>Large Language Models (LLMs) like GPT-4, PaLM, and Claude have revolutionized natural language processing. These models can generate human-like text, understand complex instructions, and even write code. Their capabilities go far beyond simple text completion, extending to reasoning, problem-solving, and creative tasks.</p>
          <p>For developers, this means a new paradigm in building AI-powered applications. Instead of training specialized models for each task, a single large model can be prompted or fine-tuned to handle various functions, dramatically reducing development time and resource requirements.</p>
          
          <h2>Multimodal AI Systems</h2>
          <p>While early AI systems focused on single modalities (text, images, or audio), the trend is now shifting toward multimodal systems that can process and generate multiple types of data. Models like DALL-E 3, Midjourney, and GPT-4V can understand the relationship between text and images, creating new possibilities for creative tools and applications.</p>
          <p>This convergence of modalities enables more intuitive AI systems that can interact with users in more natural ways, understanding context across different types of inputs.</p>
          
          <h2>Democratization of AI Development</h2>
          <p>Perhaps the most significant trend is the democratization of AI development. With tools like Hugging Face, TensorFlow.js, and various cloud AI services, developers with minimal machine learning expertise can now integrate powerful AI capabilities into their applications.</p>
          <p>Open-source models and frameworks have further accelerated this trend, allowing smaller companies and individual developers to compete with tech giants in building innovative AI solutions.</p>
          
          <h2>Ethical AI and Responsible Development</h2>
          <p>As AI becomes more powerful and widespread, the importance of ethical considerations grows. Concerns about bias, privacy, transparency, and security are driving both regulatory changes and industry practices.</p>
          <p>Developers are increasingly expected to build systems that are not just functional but also fair, accountable, and transparent. This involves careful data selection, regular auditing for bias, and designing systems with appropriate human oversight.</p>
          
          <h2>The Future: AI Agents and Autonomous Systems</h2>
          <p>Looking ahead, we're seeing the emergence of AI agents that can perform complex tasks autonomously. These systems combine large language models with planning capabilities and tool-using abilities to execute multi-step processes with minimal human intervention.</p>
          <p>From virtual assistants that can truly understand and anticipate user needs to specialized agents that can research, analyze data, and generate insights, these systems represent the next frontier in AI development.</p>
          
          <h2>Conclusion</h2>
          <p>The pace of innovation in AI shows no signs of slowing down. For developers, this means both exciting opportunities and new challenges. Those who can adapt to these trends and build responsibly will be well-positioned to create the next generation of intelligent applications.</p>
          <p>As we continue this journey, collaboration between technical experts, domain specialists, and ethicists will be essential to ensure that AI development proceeds in ways that benefit humanity while minimizing potential harms.</p>
        `,
        tags: ["AI", "Future Tech", "Development", "Machine Learning"]
      };
      
      // Mock related posts
      const mockRelatedPosts = [
        {
          id: 2,
          slug: "building-jarvis-voice-code-editor",
          title: "Building JARVIS: The Voice-Controlled Code Editor",
          excerpt: "A deep dive into the technical challenges and solutions involved in creating a hands-free programming environment powered by AI and speech recognition.",
          coverImage: "/blog/jarvis-editor.jpg",
          publishedAt: "April 28, 2023",
          tags: ["Project", "AI", "Accessibility"],
          readTime: "12 min read"
        },
        {
          id: 3,
          slug: "sustainable-tech-through-innovation",
          title: "Sustainable Technology Through Innovation",
          excerpt: "How combining mechanical engineering with modern technology can lead to more sustainable solutions for our most pressing environmental challenges.",
          coverImage: "/blog/sustainable-tech.jpg",
          publishedAt: "March 10, 2023",
          tags: ["Sustainability", "Innovation", "Engineering"],
          readTime: "7 min read"
        }
      ];
      
      // Simulate API delay
      setTimeout(() => {
        setPost(mockPost);
        setRelatedPosts(mockRelatedPosts);
        setLoading(false);
      }, 500);
    };
    
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="bg-gray-900 min-h-screen pt-24">
          <div className="container max-w-4xl mx-auto px-4 py-12">
            <div className="animate-pulse">
              <div className="h-10 bg-gray-800 w-3/4 rounded-lg mb-8"></div>
              <div className="h-96 bg-gray-800 rounded-lg mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-800 w-full rounded"></div>
                <div className="h-4 bg-gray-800 w-full rounded"></div>
                <div className="h-4 bg-gray-800 w-3/4 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="bg-gray-900 min-h-screen pt-24">
          <div className="container max-w-4xl mx-auto px-4 py-12">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-white mb-4">Blog Post Not Found</h1>
              <p className="text-gray-400 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
              <Link to="/#blog" className="btn btn-primary">
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Dhiraj T H</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={`${post.title} | Dhiraj T H`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.coverImage} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <Navbar />
      
      <main className="bg-gray-900 pt-24 pb-20">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Back Link */}
          <Link 
            to="/#blog" 
            className="inline-flex items-center gap-2 text-primary hover:underline mb-6"
          >
            <FaChevronLeft size={12} /> Back to Blog
          </Link>
          
          {/* Post Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-8">
              <div className="flex items-center gap-1">
                <FaCalendarAlt />
                <span>{post.publishedAt}</span>
              </div>
              <div className="flex items-center gap-1">
                <FaClock />
                <span>{post.readTime}</span>
              </div>
            </div>
            
            {/* Cover Image */}
            <div className="rounded-lg overflow-hidden mb-8">
              <img 
                src={post.coverImage} 
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
            
            {/* Author Info */}
            <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-lg mb-8">
              <img 
                src={post.author.avatar} 
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-primary"
              />
              <div>
                <h3 className="text-white font-medium">{post.author.name}</h3>
                <p className="text-gray-400 text-sm">{post.author.bio}</p>
              </div>
            </div>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, index) => (
                <span 
                  key={index} 
                  className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
          
          {/* Post Content */}
          <motion.div 
            className="prose prose-lg max-w-none prose-invert prose-h2:text-2xl prose-h2:font-semibold prose-h2:text-white prose-h2:mt-10 prose-h2:mb-4 prose-p:text-gray-300 prose-p:mb-6 prose-p:leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          
          {/* Share Buttons */}
          <div className="mt-12 mb-16 pt-6 border-t border-gray-800">
            <div className="flex items-center gap-4">
              <span className="text-white font-medium flex items-center gap-2">
                <FaShareAlt className="text-primary" /> Share:
              </span>
              <div className="flex gap-3">
                <button className="text-gray-400 hover:text-[#1DA1F2] transition-colors">
                  <FaTwitter size={20} />
                </button>
                <button className="text-gray-400 hover:text-[#4267B2] transition-colors">
                  <FaFacebook size={20} />
                </button>
                <button className="text-gray-400 hover:text-[#0077B5] transition-colors">
                  <FaLinkedin size={20} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Related Posts</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <motion.article 
                    key={relatedPost.id}
                    className="bg-gray-800 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/30 transition-all hover:-translate-y-1 flex flex-col"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={relatedPost.coverImage} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        />
                      </div>
                    </Link>
                    
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex justify-between items-center text-xs text-gray-400 mb-2">
                        <div className="flex items-center gap-1">
                          <FaCalendarAlt />
                          <span>{relatedPost.publishedAt}</span>
                        </div>
                        <span>{relatedPost.readTime}</span>
                      </div>
                      
                      <h4 className="text-lg font-medium text-white mb-2">
                        <Link 
                          to={`/blog/${relatedPost.slug}`}
                          className="hover:text-primary transition-colors"
                        >
                          {relatedPost.title}
                        </Link>
                      </h4>
                      
                      <p className="text-gray-400 text-sm mb-4 flex-1">
                        {relatedPost.excerpt}
                      </p>
                      
                      <Link 
                        to={`/blog/${relatedPost.slug}`}
                        className="text-primary hover:underline text-sm inline-flex items-center gap-1 mt-auto"
                      >
                        Read More <FaChevronLeft className="rotate-180" size={10} />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default BlogPost; 